import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserPanel } from '../layout/Sidebar';
import { ContextMenu, MenuItem, MenuDivider, Icons } from '../dm/Menu';
import Modal, { ModalButton } from '../dm/Modal';
import { Collapse } from '../motion';
import { useServer, useServers } from './ServersContext';
import { canDo, channelUnread, orderedStructure } from './format';
import InviteDialog from './InviteDialog';
import ServerSettings from './ServerSettings';
import { ReportServerDialog } from './ReportDialogs';

const COLLAPSED_KEY = (nodeId) => `collapsed_categories:${nodeId}`;

function loadCollapsed(nodeId) {
  try { return new Set(JSON.parse(localStorage.getItem(COLLAPSED_KEY(nodeId)) || '[]')); } catch { return new Set(); }
}

const ERROR_TEXT = {
  not_member: 'You are not a member of this server anymore.',
  blocked: 'This server was blocked by BLOBL.',
  unavailable: 'This server is not available anymore.',
  outdated: 'This server runs an outdated version of BLOBL Node. Ask the host to update it.',
  offline: "Can't reach this server right now.",
  error: 'Something went wrong loading this server.',
};

// Channel list of a server (replaces the DM list while a server is open)
export default function ServerSidebar({ nodeId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { touch, loadServer, removeServer, leaveServer } = useServers();
  const { node, st } = useServer(nodeId);
  const [menu, setMenu] = useState(null);
  const [dialog, setDialog] = useState(null); // 'invite' | 'settings' | 'leave' | 'report'
  const [collapsed, setCollapsed] = useState(() => loadCollapsed(nodeId));
  const [busy, setBusy] = useState(false);
  const activeChannel = Number(location.pathname.match(/^\/servers\/[a-z0-9]{10}\/(\d+)/)?.[1]) || null;

  useEffect(() => { setCollapsed(loadCollapsed(nodeId)); }, [nodeId]);
  useEffect(() => { if (st.status !== 'error') touch(nodeId); }, [nodeId, st.status, touch]);

  const toggle = (catId) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId); else next.add(catId);
      try { localStorage.setItem(COLLAPSED_KEY(nodeId), JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const name = st.info?.name || node?.name || 'Server';
  const role = st.me?.role;
  const settings = st.info?.settings;
  const canInvite = canDo(role, 'create_invite', settings);
  const canManage = ['manage_server', 'manage_channels', 'view_reports', 'ban_members'].some(a => canDo(role, a));
  const openReports = st.openReports || 0;

  const leave = async () => {
    setBusy(true);
    try {
      await leaveServer(nodeId);
      navigate('/dm', { replace: true });
    } catch {
      setBusy(false);
    }
  };

  const { loose, groups } = orderedStructure(st.categories, st.channels);
  const channelRow = (c) => {
    const u = st.unread[c.id];
    const unread = channelUnread(u) && c.id !== activeChannel;
    const active = c.id === activeChannel;
    return (
      <button
        key={c.id}
        type="button"
        onClick={() => navigate(`/servers/${nodeId}/${c.id}`)}
        className={`group flex w-full cursor-pointer items-center gap-1.5 rounded-sm border-0 px-2 py-[7px] text-left font-body text-[15px] ${
          active ? 'bg-surface-3 text-text' : unread ? 'bg-transparent font-semibold text-text hover:bg-surface-2' : 'bg-transparent text-text-muted hover:bg-surface-2 hover:text-text'
        }`}
      >
        <span className={`w-4 shrink-0 text-center text-lg leading-none ${active || unread ? 'text-text-muted' : 'text-text-dim'}`}>#</span>
        <span className="min-w-0 flex-1 truncate">{c.name}</span>
        {u?.mentions > 0 && c.id !== activeChannel && (
          <span className="flex h-4 min-w-[16px] shrink-0 items-center justify-center rounded-full bg-red px-1 text-[10px] font-extrabold text-white">{u.mentions > 99 ? '99+' : u.mentions}</span>
        )}
      </button>
    );
  };

  return (
    <aside className="flex h-full min-w-0 flex-1 flex-col bg-surface md:w-sidebar md:flex-none">
      <button
        type="button"
        disabled={st.status !== 'ready'}
        onClick={e => { const r = e.currentTarget.getBoundingClientRect(); setMenu({ x: r.left + 20, y: r.bottom + 4 }); }}
        className="flex h-12 shrink-0 cursor-pointer items-center gap-2 border-0 border-b border-solid border-black bg-transparent px-4 text-left hover:bg-surface-2 disabled:cursor-default disabled:hover:bg-transparent"
      >
        <span className="min-w-0 flex-1 truncate font-display text-[15px] font-extrabold text-text">{name}</span>
        {openReports > 0 && (
          <span title={`${openReports} open ${openReports === 1 ? 'report' : 'reports'}`} className="flex h-4 min-w-[16px] shrink-0 items-center justify-center rounded-full bg-red px-1 text-[10px] font-extrabold text-white">
            {openReports > 99 ? '99+' : openReports}
          </span>
        )}
        {st.status === 'ready' && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-text-muted"><polyline points="6 9 12 15 18 9" /></svg>
        )}
      </button>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-3 pt-3">
        {st.status === 'loading' && (
          <div className="flex justify-center py-8">
            <div className="h-5 w-5 animate-spin rounded-full border-[3px] border-solid border-border border-t-purple" />
          </div>
        )}

        {st.status === 'error' && (
          <div className="rounded bg-surface-2 px-3 py-3 text-sm leading-relaxed text-text-muted">
            {st.error?.reason ? st.error.message : ERROR_TEXT[st.error?.kind] || ERROR_TEXT.error}
            <div className="mt-3 flex flex-wrap gap-2">
              {['offline', 'error'].includes(st.error?.kind) && (
                <button type="button" onClick={() => loadServer(nodeId)} className="cursor-pointer rounded-pill border-0 bg-surface-3 px-3 py-1.5 font-display text-xs font-bold text-text">Try again</button>
              )}
              {['not_member', 'blocked', 'unavailable'].includes(st.error?.kind) && (
                <button type="button" onClick={() => { removeServer(nodeId); navigate('/dm', { replace: true }); }} className="cursor-pointer rounded-pill border-0 bg-surface-3 px-3 py-1.5 font-display text-xs font-bold text-text">Remove from list</button>
              )}
            </div>
          </div>
        )}

        {st.status === 'ready' && (
          <>
            {loose.map(channelRow)}
            {groups.map(({ category, channels }) => {
              const open = !collapsed.has(category.id);
              return (
                <div key={category.id} className="mt-3">
                  <button
                    type="button"
                    onClick={() => toggle(category.id)}
                    className="flex w-full cursor-pointer items-center gap-1 border-0 bg-transparent px-1 py-1 text-left text-[11px] font-extrabold uppercase tracking-wider text-text-dim hover:text-text-muted"
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 transition-transform duration-200" style={{ transform: open ? 'none' : 'rotate(-90deg)' }}><polyline points="6 9 12 15 18 9" /></svg>
                    <span className="min-w-0 flex-1 truncate">{category.name}</span>
                  </button>
                  <Collapse open={open}>
                    <div className="flex flex-col gap-px pt-0.5">{channels.map(channelRow)}</div>
                  </Collapse>
                  {/* Collapsed: unread or active channels stay visible, like Discord */}
                  {!open && channels.filter(c => c.id === activeChannel || channelUnread(st.unread[c.id])).map(channelRow)}
                </div>
              );
            })}
          </>
        )}
      </div>

      <UserPanel />

      {menu && (
        <ContextMenu x={menu.x} y={menu.y} onClose={() => setMenu(null)}>
          {canInvite && <MenuItem icon={Icons.userPlus} onClick={() => { setMenu(null); setDialog('invite'); }}>Invite people</MenuItem>}
          {canManage && <MenuItem icon={Icons.edit} onClick={() => { setMenu(null); setDialog('settings'); }}>Server settings</MenuItem>}
          {role !== 'owner' && (
            <>
              {(canInvite || canManage) && <MenuDivider />}
              <MenuItem icon={Icons.flag} danger onClick={() => { setMenu(null); setDialog('report'); }}>Report server</MenuItem>
              <MenuItem icon={Icons.logout} danger onClick={() => { setMenu(null); setDialog('leave'); }}>Leave server</MenuItem>
            </>
          )}
        </ContextMenu>
      )}

      {dialog === 'invite' && <InviteDialog nodeId={nodeId} onClose={() => setDialog(null)} />}
      {dialog === 'settings' && <ServerSettings nodeId={nodeId} onClose={() => setDialog(null)} />}
      {dialog === 'report' && <ReportServerDialog nodeId={nodeId} name={name} onClose={() => setDialog(null)} />}
      {dialog === 'leave' && (
        <Modal
          title={`Leave ${name}?`}
          onClose={() => setDialog(null)}
          footer={<><ModalButton onClick={() => setDialog(null)}>Cancel</ModalButton><ModalButton danger disabled={busy} onClick={leave}>{busy ? '…' : 'Leave'}</ModalButton></>}
        >
          <div className="text-sm leading-relaxed text-text-muted">You can only come back with a new invite link. Your messages stay on the server.</div>
        </Modal>
      )}
    </aside>
  );
}
